/**
 * Scout MVP — "Zapytaj Scouta": dialog z wywiadem adaptacyjnym.
 * Scout klasyfikuje intencję, dopytuje o braki, pokazuje diagnozę
 * i zapisuje gotowe materiały do Skrzynki (Do akceptacji).
 */
import { useEffect, useRef, useState } from 'react';
import { API } from '@/utils/api';
import {
  MessageCircleQuestion, Send, Loader2, Sparkles, Inbox, LifeBuoy,
  Check, RotateCcw, Stethoscope, Globe, Paperclip, UserPlus,
} from 'lucide-react';
import { TYPE_META } from '@/pages/ScoutStudio';

const panel = 'rounded-2xl border border-[var(--sc-border)] bg-[var(--sc-surface)]';
const chip = 'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold';

const SUGGESTIONS = [
  'Nie wiem, od czego zacząć. Poprowadź mnie.',
  'Chcę zdobyć więcej klientów dla mojej firmy.',
  'Potrzebuję przygotować ofertę dla klienta.',
  'Mam wiadomość od klienta i nie wiem, co odpisać.',
];

export default function ScoutZapytaj({ client, opening, onNavigate }) {
  const [sessionId, setSessionId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const endRef = useRef(null);
  const startedTs = useRef(0);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { setSessionId(null); setMsgs([]); setErr(''); }, [client.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [msgs, loading]);

  useEffect(() => {
    if (opening?.text && opening.ts !== startedTs.current) {
      startedTs.current = opening.ts;
      begin(opening.text);
    }
  }, [opening]); // eslint-disable-line react-hooks/exhaustive-deps

  const begin = async (text) => {
    if (loading) return;
    setLoading(true); setErr('');
    setSessionId(null);
    setMsgs([{ role: 'user', text }]);
    try {
      const res = await fetch(`${API}/scout/dialog/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: client.id, opening: text }),
      });
      const d = await res.json();
      if (d?.ok) { setSessionId(d.session_id); setMsgs((m) => [...m, { role: 'scout', turn: d.turn }]); }
      else setErr(d?.message || 'Nie udało się rozpocząć rozmowy.');
    } catch { setErr('Błąd połączenia. Spróbuj ponownie.'); }
    finally { setLoading(false); }
  };

  const send = async (forcedText) => {
    const text = (typeof forcedText === 'string' ? forcedText : input).trim();
    if (!text || loading) return;
    if (!sessionId) { setInput(''); begin(text); return; }
    setInput(''); setLoading(true); setErr('');
    setMsgs((m) => [...m, { role: 'user', text }]);
    try {
      const res = await fetch(`${API}/scout/dialog/message`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, text }),
      });
      const d = await res.json();
      if (d?.ok) setMsgs((m) => [...m, { role: 'scout', turn: d.turn }]);
      else setErr(d?.message || 'Nie udało się przygotować odpowiedzi.');
    } catch { setErr('Błąd połączenia. Spróbuj ponownie.'); }
    finally { setLoading(false); }
  };

  const reset = () => { setSessionId(null); setMsgs([]); setErr(''); };

  const uploadFile = async (file) => {
    if (!file || uploading || loading) return;
    setUploading(true); setErr('');
    setMsgs((m) => [...m, { role: 'user', text: `Wgrywam materiał do analizy: ${file.name}` }]);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('client_id', client.id);
      fd.append('session_id', sessionId || '');
      const res = await fetch(`${API}/scout/dialog/upload`, { method: 'POST', body: fd });
      const d = await res.json();
      if (d?.ok) { setSessionId(d.session_id); setMsgs((m) => [...m, { role: 'scout', turn: d.turn }]); }
      else setErr(d?.message || 'Nie udało się przeanalizować pliku.');
    } catch { setErr('Błąd połączenia podczas wysyłania pliku.'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="space-y-5" data-testid="view-zapytaj">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><MessageCircleQuestion className="w-5 h-5 text-[#FF8A3D]" /> Zapytaj Scouta</h2>
          <p className="text-xs text-[var(--sc-t45)] mt-0.5 ml-7">Opisz sprawę prostym językiem. Dopytam o braki, zrobię diagnozę i przygotuję konkret do akceptacji.</p>
        </div>
        {msgs.length > 0 && (
          <button onClick={reset} data-testid="dialog-reset-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--sc-border2)] px-3 py-2 text-xs font-semibold text-[var(--sc-t50)] hover:bg-[var(--sc-hover)] transition shrink-0">
            <RotateCcw className="w-3.5 h-3.5" /> Nowa rozmowa
          </button>
        )}
      </div>

      {/* START — sugestie */}
      {msgs.length === 0 && (
        <div className={`${panel} p-6`} data-testid="dialog-empty">
          <p className="text-sm text-[var(--sc-t60)] mb-4">Przykłady, od czego możesz zacząć:</p>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => begin(s)} data-testid={`dialog-suggestion-${SUGGESTIONS.indexOf(s)}`}
                className="rounded-xl border border-[var(--sc-border2)] bg-[var(--sc-surface2)] px-4 py-3 text-left text-sm text-[var(--sc-t70)] hover:border-orange-400/40 hover:text-[var(--sc-t100)] transition">
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ROZMOWA */}
      {msgs.length > 0 && (
        <div className="space-y-4" data-testid="dialog-messages">
          {msgs.map((m, i) => (m.role === 'user'
            ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl rounded-br-md bg-[#F26A21] text-white px-4 py-3 text-sm whitespace-pre-wrap" data-testid={`msg-user-${i}`}>{m.text}</div>
              </div>
            )
            : <ScoutTurn key={i} idx={i} turn={m.turn} clientId={client.id} onNavigate={onNavigate}
                isLast={i === msgs.length - 1 && !loading && !uploading} onChoice={(t) => send(t)} />
          ))}
          {(loading || uploading) && (
            <div className="flex items-center gap-2.5 text-sm text-[var(--sc-t45)]" data-testid="dialog-loading">
              <Loader2 className="w-4 h-4 animate-spin text-[#FF8A3D]" /> {uploading ? 'Scout czyta materiał…' : 'Scout analizuje…'}
            </div>
          )}
          <div ref={endRef} />
        </div>
      )}

      {err && <p className="text-sm text-red-400" data-testid="dialog-error">{err}</p>}

      {/* INPUT */}
      <div className={`${panel} p-3 flex gap-2 sticky bottom-4`}>
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.md,.csv" className="hidden"
          onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="dialog-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading || loading} data-testid="dialog-attach-btn"
          title="Wgraj materiał do analizy (PDF, screen, zdjęcie, notatka)"
          className="self-end w-11 h-11 shrink-0 rounded-xl border border-[var(--sc-border2)] flex items-center justify-center text-[var(--sc-t50)] hover:text-orange-300 hover:border-orange-400/40 transition disabled:opacity-50">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        </button>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={msgs.length === 0 ? 'np. Robię remonty w Krakowie, jak zdobyć klientów?' : 'Odpowiedz Scoutowi… (Enter = wyślij)'}
          rows={2}
          data-testid="dialog-input"
          className="flex-1 resize-none rounded-xl bg-[var(--sc-input)] border border-[var(--sc-border2)] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400/50"
        />
        <button onClick={() => send()} disabled={loading || !input.trim()} data-testid="dialog-send-btn"
          className="self-end inline-flex items-center gap-2 rounded-xl bg-[#F26A21] hover:bg-[#FF7C33] px-4 py-2.5 text-sm font-bold text-white transition disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
      <p className="text-[11px] text-[var(--sc-t35)] text-center -mt-2">Spinacz = wgraj PDF, screen, zdjęcie lub notatkę (maks 8 MB). Scout wyjaśni i przygotuje odpowiedź.</p>
    </div>
  );
}

function ScoutTurn({ turn, idx, clientId, onNavigate, isLast, onChoice }) {
  const [leadSaved, setLeadSaved] = useState(false);
  const saveLead = async () => {
    if (leadSaved || !turn.lead) return;
    try {
      const res = await fetch(`${API}/scout/leads/add`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, ...turn.lead }),
      });
      const d = await res.json();
      if (d?.ok) setLeadSaved(true);
    } catch { /* noop */ }
  };
  return (
    <div className="space-y-3 max-w-[92%]" data-testid={`msg-scout-${idx}`}>
      <div className={`${panel} rounded-bl-md px-4 py-3`}>
        <p className="text-sm text-[var(--sc-t85)] leading-relaxed whitespace-pre-wrap">{turn.say}</p>
        {turn.questions?.length > 0 && (
          <ul className="mt-3 space-y-1.5" data-testid={`turn-questions-${idx}`}>
            {turn.questions.map((q, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[var(--sc-t70)]">
                <span className="w-5 h-5 rounded-md bg-orange-500/15 text-[#FF8A3D] text-[11px] font-extrabold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                {q}
              </li>
            ))}
          </ul>
        )}
        {turn.why && <p className="text-[11px] text-[var(--sc-t40)] mt-2.5 italic">{turn.why}</p>}
      </div>

      {/* SZYBKIE ODPOWIEDZI */}
      {isLast && turn.choices?.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`turn-choices-${idx}`}>
          {turn.choices.map((c) => (
            <button key={c} onClick={() => onChoice?.(c)} data-testid={`choice-${turn.choices.indexOf(c)}`}
              className="rounded-xl border border-orange-400/40 bg-orange-500/10 px-3.5 py-2 text-sm font-semibold text-orange-300 hover:bg-orange-500/20 transition">
              {c}
            </button>
          ))}
        </div>
      )}

      {/* BRAKI DO PROFESJONALNEJ OFERTY */}
      {turn.offer_gaps?.length > 0 && (
        <div className={`${panel} p-4 border-amber-400/30`} data-testid={`turn-offer-gaps-${idx}`}>
          <div className="text-sm font-bold mb-2 text-amber-300">Do wersji profesjonalnej brakuje:</div>
          <ul className="space-y-1">
            {turn.offer_gaps.map((g, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[var(--sc-t70)]">
                <span className="w-1 h-1 rounded-full mt-2 shrink-0 bg-amber-400" /> {g}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* DIAGNOZA */}
      {turn.diagnosis && (
        <div className={`${panel} p-4`} data-testid={`turn-diagnosis-${idx}`}>
          <div className="text-sm font-bold mb-3 flex items-center gap-2"><Stethoscope className="w-4 h-4 text-[#FF8A3D]" /> Diagnoza Scouta</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <DiagList title="Co masz" color="#10B981" items={turn.diagnosis.have} />
            <DiagList title="Czego brakuje" color="#F59E0B" items={turn.diagnosis.missing} />
            <DiagList title="Co blokuje efekt" color="#EF4444" items={turn.diagnosis.blockers} />
            <DiagList title="Co zrobić teraz" color="#3B82F6" items={turn.diagnosis.now} />
          </div>
        </div>
      )}

      {/* MATERIAŁY */}
      {turn.artifacts?.map((a) => {
        const meta = TYPE_META[a.type] || TYPE_META.inne;
        return (
          <div key={a.id} className={`${panel} p-4 border-orange-400/25`} style={{ background: 'var(--sc-counter-grad)' }} data-testid={`turn-artifact-${a.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[#FF8A3D]" />
              <span className="text-sm font-bold flex-1">{a.title}</span>
              <span className={chip} style={{ background: `${meta.color}22`, color: meta.color }}>{meta.label}</span>
              {a.channel && <span className={`${chip} bg-[var(--sc-surface3)] text-[var(--sc-t60)]`}><Globe className="w-3 h-3" /> {a.channel}</span>}
            </div>
            <p className="text-sm text-[var(--sc-t80)] leading-relaxed whitespace-pre-wrap rounded-xl bg-[var(--sc-side)] border border-[var(--sc-border)] p-3.5">{a.content}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Zapisano w Skrzynce — Do akceptacji</span>
              <button onClick={() => onNavigate?.('skrzynka')} data-testid={`artifact-open-inbox-${a.id}`}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-[#F26A21] hover:bg-[#FF7C33] px-3 py-1.5 text-xs font-bold text-white transition">
                <Inbox className="w-3.5 h-3.5" /> Otwórz Skrzynkę
              </button>
            </div>
          </div>
        );
      })}

      {/* ZADANIA DODANE DO PLANU */}
      {turn.plan?.length > 0 && (
        <div className={`${panel} p-4`} data-testid={`turn-plan-${idx}`}>
          <div className="text-sm font-bold mb-2 flex items-center gap-2 text-emerald-400"><Check className="w-4 h-4" /> Dodano do planu działań ({turn.plan.length})</div>
          <ul className="space-y-1">
            {turn.plan.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[var(--sc-t70)]">
                <span className="w-1 h-1 rounded-full mt-2 shrink-0 bg-emerald-400" /> {p}
              </li>
            ))}
          </ul>
          <button onClick={() => onNavigate?.('plan')} data-testid={`turn-plan-open-${idx}`}
            className="mt-2.5 text-xs font-semibold text-orange-300 hover:underline">Otwórz plan działań →</button>
        </div>
      )}

      {/* PROPOZYCJA LEADA */}
      {turn.lead && (
        <div className={`${panel} p-4 flex flex-wrap items-center gap-3`} data-testid={`turn-lead-${idx}`}>
          <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-400/25 flex items-center justify-center shrink-0">
            <UserPlus className="w-4 h-4 text-emerald-300" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-bold">Potencjalny klient: {turn.lead.name}</div>
            <div className="text-xs text-[var(--sc-t50)] mt-0.5">{[turn.lead.contact, turn.lead.note].filter(Boolean).join(' · ')}</div>
          </div>
          <button onClick={saveLead} disabled={leadSaved} data-testid={`turn-lead-save-${idx}`}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition ${leadSaved ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-400/30' : 'bg-[#F26A21] hover:bg-[#FF7C33] text-white'}`}>
            {leadSaved ? <Check className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />} {leadSaved ? 'Zapisano w Leadach' : 'Zapisz jako lead'}
          </button>
        </div>
      )}

      {/* APISAI */}
      {turn.apisai?.show && (
        <div className={`${panel} p-4 flex items-start gap-3`} data-testid={`turn-apisai-${idx}`}>
          <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-400/25 flex items-center justify-center shrink-0">
            <Globe className="w-4 h-4 text-blue-300" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold">{turn.apisai.what === 'katalog' ? 'Katalog APISAI może tu pomóc' : 'Strona w APISAI może tu pomóc'}</div>
            <p className="text-xs text-[var(--sc-t50)] mt-0.5">{turn.apisai.reason}</p>
            <p className="text-[11px] text-[var(--sc-t35)] mt-1.5">To propozycja wynikająca z diagnozy — decyzja należy do Ciebie.</p>
          </div>
        </div>
      )}

      {/* HANDOFF */}
      {turn.handoff && (
        <div className={`${panel} p-4 flex items-start gap-3 border-amber-400/30`} data-testid={`turn-handoff-${idx}`}>
          <LifeBuoy className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
          <p className="text-sm text-[var(--sc-t70)]">To pytanie wymaga sprawdzenia przez człowieka. <span className="text-amber-300 font-semibold">Przekazałem je opiekunowi APIS</span> — wrócimy z odpowiedzią.</p>
        </div>
      )}
    </div>
  );
}

function DiagList({ title, color, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="text-xs font-bold mb-1.5" style={{ color }}>{title}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[var(--sc-t70)]">
            <span className="w-1 h-1 rounded-full mt-2 shrink-0" style={{ background: color }} /> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
